import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { insurancePolicyPartiesApi } from '../api/insurancePolicyParties';
import { ApiError } from '../api/client';
import type {
  InsurancePolicyParty,
  InsurancePolicyPartyInput,
  InsurancePolicyPartyRole,
  Page,
  UUID,
  WriteResult,
} from '../api/types';

export const ippKeys = {
  all: ['insurance-policy-parties'] as const,
  list: (
    policyId?: UUID, role?: InsurancePolicyPartyRole, personId?: UUID,
    includeDeleted?: boolean, onlyDeleted?: boolean,
  ) =>
    ['insurance-policy-parties', 'list', policyId, role, personId,
      includeDeleted ?? false, onlyDeleted ?? false] as const,
  detail: (id: UUID) => ['insurance-policy-parties', 'detail', id] as const,
};

export function useInsurancePolicyPartiesList(
  policyId?: UUID,
  role?: InsurancePolicyPartyRole,
  personId?: UUID,
  // #39 (2026-06-11): two-bucket pills on the list page. Also fixes the
  // latent #362-era bug where the page's "Include deleted" switch was
  // wired to state this hook never received.
  // #46 (2026-06-12): per-column filters (C-132) — roles is the multi
  // variant (the positional `role` stays for legacy single-role callers).
  opts?: {
    includeDeleted?: boolean; onlyDeleted?: boolean; enabled?: boolean;
    roles?: InsurancePolicyPartyRole[];
    personQ?: string; policyQ?: string; designations?: string[];
    /** P41: server-side sort; and the paging the list page always meant to send
     *  (found 2026-10-15: limit / offset never reached the server, so every page
     *  showed rows 1–50). */
    sort?: string; limit?: number; offset?: number;
  },
) {
  return useQuery<Page<InsurancePolicyParty>, ApiError>({
    queryKey: [
      ...ippKeys.list(policyId, role, personId, opts?.includeDeleted, opts?.onlyDeleted),
      { roles: opts?.roles, personQ: opts?.personQ, policyQ: opts?.policyQ,
        designations: opts?.designations, sort: opts?.sort, limit: opts?.limit, offset: opts?.offset },
    ],
    queryFn: () => insurancePolicyPartiesApi.list({
      policy_id: policyId,
      role: opts?.roles?.length ? opts.roles : (role ? [role] : undefined),
      person_id: personId,
      include_deleted: opts?.includeDeleted || undefined,
      only_deleted: opts?.onlyDeleted || undefined,
      person_q: opts?.personQ || undefined,
      policy_q: opts?.policyQ || undefined,
      designation: opts?.designations?.length ? opts.designations : undefined,
      sort: opts?.sort,
    }, opts?.limit ?? 50, opts?.offset ?? 0),
    enabled: opts?.enabled ?? true,
    // #46: keep previous rows visible during filter-driven refetches.
    placeholderData: keepPreviousData,
  });
}

export function useInsurancePolicyParty(id: UUID | undefined) {
  return useQuery<InsurancePolicyParty, ApiError>({
    queryKey: ippKeys.detail(id ?? ''),
    queryFn: () => insurancePolicyPartiesApi.get(id!),
    enabled: !!id,
  });
}

// A policy's completeness rules count its parties (_has_policy_holder / _life_insured /
// _beneficiary), so every party change also refreshes the /compliance queries — the
// policy page's missing-field banner and tab counts (C-204 family, 2026-10-10).
export function useCreateInsurancePolicyParty() {
  const qc = useQueryClient();
  return useMutation<WriteResult<InsurancePolicyParty>, ApiError, InsurancePolicyPartyInput>({
    mutationFn: insurancePolicyPartiesApi.create,
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ippKeys.all });
      qc.invalidateQueries({ queryKey: ['compliance'] });
      if (!r.pending) qc.setQueryData(ippKeys.detail(r.entity.id), r.entity);
    },
  });
}

export function useUpdateInsurancePolicyParty(id: UUID) {
  const qc = useQueryClient();
  return useMutation<
    WriteResult<InsurancePolicyParty>,
    ApiError,
    Partial<InsurancePolicyPartyInput>
  >({
    mutationFn: (p) => insurancePolicyPartiesApi.update(id, p),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ippKeys.all });
      qc.invalidateQueries({ queryKey: ['compliance'] });
      if (!r.pending) qc.setQueryData(ippKeys.detail(id), r.entity);
    },
  });
}

export function useDeleteInsurancePolicyParty() {
  const qc = useQueryClient();
  return useMutation<WriteResult<InsurancePolicyParty>, ApiError, UUID>({
    mutationFn: insurancePolicyPartiesApi.delete,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ippKeys.all });
      qc.invalidateQueries({ queryKey: ['compliance'] });
    },
  });
}

export function useRestoreInsurancePolicyParty() {
  const qc = useQueryClient();
  return useMutation<WriteResult<InsurancePolicyParty>, ApiError, UUID>({
    mutationFn: insurancePolicyPartiesApi.restore,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ippKeys.all });
      qc.invalidateQueries({ queryKey: ['compliance'] });
    },
  });
}
